import { Box, Modal, Pagination, useMediaQuery } from "@mui/material";
import React, { useEffect, useState, useRef } from "react";
import { RiDeleteBin6Fill, RiEdit2Fill } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  getAllCategories,
  createCategory,
  updateCategory,
  deleteCategory
} from "../Redux/Slice/category.slice";
import { FaPlus, FaSearch, FaTimes } from "react-icons/fa";
import { motion } from "framer-motion";

export default function Category() {
  const [categoryData, setCategoryData] = useState("");
  const [delOpen, setDelOpen] = useState(false);
  const dispatch = useDispatch();
  const [createopen, setCreateopen] = useState(false);
  const category = useSelector(state => state.category.categories);
  const loading = useSelector(state => state.category.loading);
  const isSmallScreen = useMediaQuery("(max-width:425px)");
  const fileInputRef = useRef(null);
  const [searchValue, setSearchValue] = useState('');

  const validationSchema = Yup.object({
    categoryName: Yup.string().required("Category name is required"),
    category_description: Yup.string().required("Category description is required"),
    category_image: Yup.mixed()
      .test(
        "fileSize",
        "File size is too large, must be 2MB or less",
        function (value) {
          if (!value) return true;
          if (typeof value === "string") return true;
          return value.size <= 2 * 1024 * 1024;
        }
      )
      .test("fileFormat", "Unsupported Format", function (value) {
        if (!value) return true;
        if (typeof value === "string") return true;
        return ["image/jpeg", "image/png", "image/gif", "image/webp"].includes(value.type);
      }),
  });

  const formik = useFormik({
    initialValues: {
      categoryName: "",
      category_description: "",
      category_image: null,
      isActive: true,
    },
    validationSchema,
    onSubmit: async (values) => {
      const formData = new FormData();
      formData.append("categoryName", values.categoryName);
      formData.append("category_description", values.category_description);
      formData.append("isActive", values.isActive);

      if (values.category_image && typeof values.category_image !== "string") {
        formData.append("category_image", values.category_image);
      }

      if (categoryData) {
        dispatch(updateCategory({ _id: categoryData._id, formData }));
      } else {
        dispatch(createCategory(formData));
      }
      handleCreateClose();
    },
  });

  useEffect(() => {
    dispatch(getAllCategories({ userside: 'false' }));
  }, [dispatch]);

  const filteredData = Array.isArray(category)
    ? category.filter(data =>
      data?.categoryName?.toLowerCase().includes(searchValue.toLowerCase())
    )
    : [];

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const currentItems = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handleOpen = (data) => {
    setCreateopen(true);
    setCategoryData(data);
    if (data) {
      formik.setValues({
        categoryName: data.categoryName,
        category_description: data.category_description,
        category_image: data.category_image?.url || null,
        isActive: data.isActive ?? true,
      });
    }
  };

  const handleCreateClose = () => {
    setCreateopen(false);
    setCategoryData("");
    formik.resetForm();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Categories</h1>
          <p className="text-gray-400">Manage game categories.</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setCreateopen(true)}
          className="flex items-center justify-center gap-2 px-6 py-3 rounded-2xl bg-gradient-primary text-white font-bold shadow-lg shadow-primary/20 transition-all"
        >
          <FaPlus /> Add Category
        </motion.button>
      </div>

      <div className="bg-white/5 border border-white/10 rounded-3xl overflow-hidden backdrop-blur-sm">
        <div className="p-6 border-b border-white/5 flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="relative w-full sm:w-80 group">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 group-focus-within:text-primary transition-colors" />
            <input
              type="text"
              placeholder="Search categories..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl pl-12 pr-4 py-2.5 text-sm text-white focus:border-primary/50 outline-none transition-all"
            />
          </div>
          <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">
            Total: {filteredData.length} Categories
          </p>
        </div>

        <div className="overflow-x-auto no-scrollbar">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-white/[0.02]">
                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest">Category</th>
                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest">Description</th>
                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest text-center">Status</th>
                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {currentItems.map((item, index) => (
                <tr key={index} className="hover:bg-white/[0.02] transition-colors group">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-4">
                      <img
                        src={item.category_image?.url}
                        alt=""
                        className="w-12 h-12 rounded-xl object-cover border border-white/10"
                      />
                      <span className="text-sm font-bold text-white group-hover:text-primary transition-colors">{item.categoryName}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-xs text-gray-400 line-clamp-1 max-w-md">{item.category_description}</p>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${item.isActive ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
                      {item.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => handleOpen(item)}
                        className="p-2 rounded-lg bg-green-500/10 text-green-400 hover:bg-green-500 hover:text-white transition-all border border-green-500/20"
                      >
                        <RiEdit2Fill size={14} />
                      </button>
                      <button
                        onClick={() => { setCategoryData(item); setDelOpen(true); }}
                        className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500 hover:text-white transition-all border border-red-500/20"
                      >
                        <RiDeleteBin6Fill size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {currentItems.length === 0 && (
                <tr>
                  <td colSpan="4" className="px-8 py-12 text-center text-gray-500 italic">No categories found matching your search.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="p-6 border-t border-white/5 flex justify-end">
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(_, page) => setCurrentPage(page)}
              sx={{
                "& .MuiPaginationItem-root": { color: "#9ca3af", borderColor: "rgba(255,255,255,0.1)", borderRadius: "12px" },
                "& .Mui-selected": { backgroundColor: "rgba(98,99,237,0.2) !important", color: "#6263ED !important", borderColor: "rgba(98,99,237,0.5) !important", fontWeight: "bold" }
              }}
            />
          </div>
        )}
      </div>

      {/* Modal: Add/Edit Category */}
      <Modal open={createopen} onClose={handleCreateClose} className="flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-[#141414] border border-white/10 rounded-3xl w-full max-w-lg shadow-2xl outline-none overflow-hidden"
        >
          <div className="p-8 border-b border-white/5 flex items-center justify-between">
            <h2 className="text-2xl font-bold text-white">{categoryData ? "Edit Category" : "New Category"}</h2>
            <button onClick={handleCreateClose} className="p-2 rounded-xl bg-white/5 text-gray-400 hover:text-white transition-colors">
              <FaTimes size={20} />
            </button>
          </div>

          <form onSubmit={formik.handleSubmit} className="p-8 space-y-6">
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-widest ml-1">Category Name</label>
              <input
                name="categoryName"
                type="text"
                placeholder="Action Games"
                className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3.5 text-white focus:border-primary/50 focus:ring-1 focus:ring-primary/50 outline-none transition-all"
                onChange={formik.handleChange}
                value={formik.values.categoryName}
              />
              {formik.touched.categoryName && formik.errors.categoryName && <p className="text-red-400 text-xs ml-1">{formik.errors.categoryName}</p>}
            </div>

            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-widest ml-1">Description</label>
              <textarea
                name="category_description"
                rows="4"
                placeholder="Briefly describe this category..."
                className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3.5 text-white focus:border-primary/50 focus:ring-1 focus:ring-primary/50 outline-none transition-all resize-none"
                onChange={formik.handleChange}
                value={formik.values.category_description}
              />
              {formik.touched.category_description && formik.errors.category_description && <p className="text-red-400 text-xs ml-1">{formik.errors.category_description}</p>}
            </div>

            <div className="space-y-4">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-widest ml-1">Category Image</label>
              <div className="relative group">
                <div className="w-full h-40 rounded-2xl border-2 border-dashed border-white/10 flex flex-col items-center justify-center bg-white/5 overflow-hidden">
                  {formik.values.category_image ? (
                    <div className="relative w-full h-full">
                      <img
                        src={typeof formik.values.category_image === 'string' ? formik.values.category_image : URL.createObjectURL(formik.values.category_image)}
                        className="w-full h-full object-cover"
                        alt=""
                      />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <span className="text-white text-xs font-bold bg-white/20 backdrop-blur-md px-4 py-2 rounded-xl">Change Image</span>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-2">
                        <FaPlus />
                      </div>
                      <span className="text-xs font-bold text-gray-500 uppercase">Upload Image</span>
                    </>
                  )}
                </div>
                <input
                  type="file"
                  accept="image/*"
                  className="absolute inset-0 opacity-0 cursor-pointer"
                  onChange={(e) => formik.setFieldValue("category_image", e.target.files[0])}
                />
              </div>
              {formik.touched.category_image && formik.errors.category_image && <p className="text-red-400 text-xs ml-1">{formik.errors.category_image}</p>}
            </div>

            <div className="flex items-center gap-3 bg-white/5 p-4 rounded-2xl border border-white/10">
              <input
                type="checkbox"
                id="isActive"
                name="isActive"
                checked={formik.values.isActive}
                onChange={formik.handleChange}
                className="w-5 h-5 rounded-md border-white/10 bg-white/5 text-primary focus:ring-primary transition-all"
              />
              <label htmlFor="isActive" className="text-sm font-bold text-white cursor-pointer select-none">Visible to Users</label>
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 rounded-2xl bg-gradient-primary text-white font-bold shadow-lg shadow-primary/20 transition-all disabled:opacity-50"
              >
                {loading ? "Processing..." : categoryData ? "Update Category" : "Create Category"}
              </button>
            </div>
          </form>
        </motion.div>
      </Modal>

      {/* Modal: Delete Confirmation */}
      <Modal open={delOpen} onClose={() => setDelOpen(false)} className="flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-[#141414] border border-white/10 rounded-3xl p-8 text-center w-full max-w-[360px] shadow-2xl"
        >
          <div className="w-16 h-16 rounded-2xl bg-red-500/10 text-red-500 flex items-center justify-center mx-auto mb-6 text-2xl border border-red-500/20">
            <RiDeleteBin6Fill />
          </div>
          <h2 className="text-xl font-bold mb-2 text-white">Delete Category?</h2>
          <p className="text-gray-400 text-sm mb-8">Are you sure you want to delete <span className="text-white font-bold">"{categoryData?.categoryName}"</span>? This may affect linked games.</p>
          <div className="flex gap-4">
            <button onClick={() => setDelOpen(false)} className="flex-1 py-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors border border-white/10 text-white font-bold">Cancel</button>
            <button
              onClick={() => { dispatch(deleteCategory({ _id: categoryData._id })); setDelOpen(false); }}
              className="flex-1 py-3 rounded-xl bg-red-500 text-white font-bold shadow-[0_0_20px_rgba(239,68,68,0.3)]"
            >
              Delete
            </button>
          </div>
        </motion.div>
      </Modal>
    </motion.div>
  );
}